import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Plus, MoreVertical, Pencil, Trash2, MapPin, Phone, Mail, User, Building } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  venue_name: "",
  contact_person: "",
  contact_phone: "",
  contact_email: "",
  notes: "",
  address: { street: "", city: "", state: "", postal_code: "", country: "South Africa" },
};

const CustomerVenues = ({ customerId }) => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, venue: null });
  const [formData, setFormData] = useState(emptyForm);

  const fetchVenues = async () => {
    try {
      const res = await axios.get(`${API}/customers/${customerId}/venues`, { withCredentials: true });
      setVenues(res.data);
    } catch {
      toast.error("Failed to load venues");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVenues(); }, [customerId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        address: formData.address.street ? formData.address : null,
      };
      if (editingVenue) {
        await axios.put(`${API}/venues/${editingVenue.venue_id}`, payload, { withCredentials: true });
        toast.success("Venue updated");
      } else {
        await axios.post(`${API}/customers/${customerId}/venues`, payload, { withCredentials: true });
        toast.success("Venue added");
      }
      setSheetOpen(false);
      resetForm();
      fetchVenues();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save venue");
    }
  };

  const handleEdit = (venue) => {
    setEditingVenue(venue);
    setFormData({
      venue_name: venue.venue_name || "",
      contact_person: venue.contact_person || "",
      contact_phone: venue.contact_phone || "",
      contact_email: venue.contact_email || "",
      notes: venue.notes || "",
      address: venue.address || { street: "", city: "", state: "", postal_code: "", country: "South Africa" },
    });
    setSheetOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/venues/${deleteDialog.venue.venue_id}`, { withCredentials: true });
      toast.success("Venue deleted");
      setDeleteDialog({ open: false, venue: null });
      fetchVenues();
    } catch {
      toast.error("Failed to delete venue");
    }
  };

  const resetForm = () => {
    setEditingVenue(null);
    setFormData(emptyForm);
  };

  const setAddr = (field, value) =>
    setFormData((f) => ({ ...f, address: { ...f.address, [field]: value } }));

  if (loading) return <div className="animate-pulse h-40 bg-slate-100 rounded-sm" />;

  return (
    <Card className="rounded-sm border-slate-200">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Venues ({venues.length})</CardTitle>
        <Button size="sm" className="rounded-sm bg-slate-900 hover:bg-slate-800" onClick={() => setSheetOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Venue
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {venues.length === 0 ? (
          <div className="text-center py-12">
            <Building className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-4">No venues added yet</p>
            <Button variant="outline" className="rounded-sm" onClick={() => setSheetOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add First Venue
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {venues.map((venue) => (
              <div key={venue.venue_id} className="p-4 flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <div className="w-10 h-10 bg-blue-50 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Building className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900">{venue.venue_name}</p>
                    <div className="mt-1 space-y-1">
                      {venue.address?.street && (
                        <div className="flex items-center gap-1.5 text-sm text-slate-500">
                          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="truncate">
                            {[venue.address.street, venue.address.city, venue.address.state]
                              .filter(Boolean).join(", ")}
                          </span>
                        </div>
                      )}
                      {venue.contact_person && (
                        <div className="flex items-center gap-1.5 text-sm text-slate-500">
                          <User className="h-3.5 w-3.5 flex-shrink-0" />
                          <span>{venue.contact_person}</span>
                        </div>
                      )}
                      {venue.contact_phone && (
                        <div className="flex items-center gap-1.5 text-sm text-slate-500">
                          <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                          <span>{venue.contact_phone}</span>
                        </div>
                      )}
                      {venue.contact_email && (
                        <div className="flex items-center gap-1.5 text-sm text-slate-500">
                          <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                          <span>{venue.contact_email}</span>
                        </div>
                      )}
                      {venue.notes && (
                        <p className="text-xs text-slate-400 mt-1">{venue.notes}</p>
                      )}
                    </div>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleEdit(venue)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-red-600 focus:text-red-600"
                      onClick={() => setDeleteDialog({ open: true, venue })}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Add / Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={(o) => { setSheetOpen(o); if (!o) resetForm(); }}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editingVenue ? "Edit Venue" : "Add Venue"}</SheetTitle>
            <SheetDescription>
              {editingVenue ? "Update venue details" : "Add a new venue for this customer"}
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label>Venue Name *</Label>
              <Input
                value={formData.venue_name}
                onChange={(e) => setFormData({ ...formData, venue_name: e.target.value })}
                placeholder="e.g. Cape Town Branch, Sandton Office"
                required
                className="rounded-sm"
              />
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3 text-slate-700">Address</p>
              <div className="space-y-2">
                <Input
                  value={formData.address.street}
                  onChange={(e) => setAddr("street", e.target.value)}
                  placeholder="Street address"
                  className="rounded-sm"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input value={formData.address.city} onChange={(e) => setAddr("city", e.target.value)} placeholder="City" className="rounded-sm" />
                  <Input value={formData.address.state} onChange={(e) => setAddr("state", e.target.value)} placeholder="Province" className="rounded-sm" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={formData.address.postal_code} onChange={(e) => setAddr("postal_code", e.target.value)} placeholder="Postal code" className="rounded-sm" />
                  <Input value={formData.address.country} onChange={(e) => setAddr("country", e.target.value)} placeholder="Country" className="rounded-sm" />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3 text-slate-700">On-site Contact</p>
              <div className="space-y-2">
                <Input
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  placeholder="Contact person name"
                  className="rounded-sm"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    placeholder="Phone number"
                    className="rounded-sm"
                  />
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="Email address"
                    className="rounded-sm"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes about this venue"
                rows={2}
                className="rounded-sm"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => { setSheetOpen(false); resetForm(); }} className="flex-1 rounded-sm">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 rounded-sm bg-slate-900 hover:bg-slate-800">
                {editingVenue ? "Update" : "Add Venue"}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(o) => setDeleteDialog({ open: o, venue: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Venue</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteDialog.venue?.venue_name}"? Jobs linked to this venue will retain the venue reference but no longer be editable via this venue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-sm bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default CustomerVenues;
